<script setup lang="ts">
import ServicePageHeader from '@booking/components/ServicePageHeader.vue'
import useApi from '@booking/composables/useApi'
import { useCompanyStore } from '@booking/stores/company'
import { getServiceImages } from '@booking/utils/images'
import { joinPageTitle, usePageTitle } from '@booking/utils/usePageSettings'
import ServiceBookButton from '@sub/components/service/ServiceBookButton.vue'
import ServiceTimeDuration from '@sub/components/service/ServiceTimeDuration.vue'
import { ServiceTypes } from '@types'
import { getSeatsData } from '@utils/priceLibrary/additionalSeats'
import { sortFiles } from '@utils/services'
import Users from '~icons/cue-regular/users'

const store = useCompanyStore()
const api = useApi()
const { company, companyInfo } = storeToRefs(store)

const route = useRoute()
const serviceId = Number(route.params.id)

const { data: service, status } = useAsyncData(`${serviceId}`, async () => {
  const companyId = await store.getCompanyIdByAlias()
  if (!companyId || !serviceId) {
    navigateTo('/404')
    return
  }

  try {
    const cachedService = store.services.find(s => s.id === serviceId)
    if (cachedService) {
      return cachedService
    }
  } catch (e) {
    console.error(e)
  }

  try {
    const [, service] = await Promise.all([
      store.fetchCompany(companyId),
      api.services.getService(companyId, serviceId),
    ])
    if (service.type !== ServiceTypes.Additional && !service.isValid) {
      navigateTo('/404')
      return
    }

    service.files = sortFiles(service.files || [])
    return service
  } catch {
    navigateTo('/404')
  }
})

usePageTitle(computed(() => joinPageTitle(company.value.clientName, service.value?.title)))

const images = computed(() => (service.value ? getServiceImages(service.value) : []))

const showViewer = ref(false)

const seats = computed(() => {
  if (!service.value) {
    return []
  }
  const { min: from, max: to, includedSeats } = getSeatsData(service.value)
  const capacitySeats =
    service.value.type === ServiceTypes.Individual && from !== to
      ? $t('word.place_diapason', { from, to })
      : $t('word.place', to)
  return [
    { label: 'block.service.info.page.seats.capacity.title', text: capacitySeats, show: !!to },
    {
      label: 'block.service.info.page.seats.included.title',
      text: $t('word.place', includedSeats),
      show: !!includedSeats,
    },
  ].filter(s => s.show)
})

useSeoMeta({
  description: () => service.value?.description,
  ogTitle: () => service.value?.title,
  ogDescription: () => service.value?.description,
  ogImage: () => images.value[0],
  twitterTitle: () => service.value?.title,
  twitterDescription: () => service.value?.description,
  twitterImage: () => images.value[0],
  twitterCard: 'summary',
})

definePageMeta({
  keepalive: true,
  key: route => route.fullPath,
})
</script>

<template lang="pug">
UiLoaderCircleLoader.m-auto(v-if="status === 'pending'" label="page.services.loader")
template(v-else-if="company?.id && service")
  ServicePageHeader(label="page.services.info.service.header.title" back="/services" :logoSrc="company.logoUrl")
  .content.flex.row-24.overflow-hidden
    UiImage.main-logo.size-216.desktop(:src="images[0]" :zoom="!!images[0]" :placeholder="company.logoUrl" @click="images[0] && (showViewer = true)")
    .flex-space-between-start.col-0.w-100p
      .flex.col-10.w-100p
        .title.typ-20-28-600 {{ service.title }}
        .title.typ-14-18-400.m-t-6 {{ service.addresses?.[0]?.country }}
        UiCarousel.image-carousel.p-8-24(v-if="isLtMd")
          UiImage.s-292(v-for="image in images" :key="image" :src="image" :alt="company.clientName")
        ServiceTimeDuration.title.typ-14-18-400.m-b-6(v-if="companyInfo.settings.showServiceDuration" :service="service")
      .flex.col-20.p-16-0-0
        //.time.flex.row-16(v-if="false")
        //  UiIcon.size-16.m-t-1(:name="Clock")
        //  .flex.col-2.typ-14-18-400
        //    span Пн – Пт  09:00 – 14:00
        //    span Сб – Вс  10:00 – 18:00
        .capacity.flex.row-16(v-if="companyInfo.settings.showCapacity")
          UiIcon.size-16.m-t-1(:name="Users")
          .flex.col-2.typ-14-18-300
            .flex.row-4(v-for="seat of seats" :key="seat.label")
              span {{ $t(seat.label) }}:
              span.title {{ seat.text }}
        p.description.typ-13-17-300(v-if="service.description") {{ service.description }}
        .actions.flex
          ServiceBookButton(:service="service" :showServicePrice="companyInfo.settings.showMinServicePrice")

UiImageViewer(v-model:show="showViewer" :src="images")
</template>

<style scoped>
.title {
  color: var(--cue-content-neutral-primary);
}

.time,
.capacity {
  color: var(--cue-content-neutral-secondary);

  .icon {
    color: var(--cue-content-neutral-tertiary);
  }
}

.desktop {
  display: none;

  @screen md {
    display: unset;
  }
}

.content {
  --uno: col-24 p-16-16-20;

  @screen sm {
    --uno: p-20-24;
  }

  @screen md {
    --uno: row-24;
  }
}

.description {
  white-space: pre-line;
  color: var(--cue-content-neutral-secondary);
  margin: 0;
}

.main-logo {
  display: none;
  flex-shrink: 0;

  @screen md {
    display: unset;
  }
}

.actions {
  --uno: row-12;
  white-space: nowrap;

  @screen sm {
    --uno: row-24;
  }
}

.image-carousel {
  width: calc(100% + 48px);
  --uno: m-x-m-24;
}
</style>
